/**
 * Animation — keyframe/easing system.
 *
 * Supports:
 *  - Cubic-bezier easings (CSS-style)
 *  - Named easings: linear, ease, ease-in, ease-out, ease-in-out
 *  - Multi-property keyframes
 *  - Per-property interpolation across keyframe timeline
 *  - Direction (normal, reverse, alternate, alternate-reverse)
 *  - Fill-mode (none, forwards, backwards, both)
 *  - Iteration count + infinite
 *
 * The engine ticks each track via `tickAnimation()` each frame. Mutates the
 * node's properties in place. The compiler then picks them up on the next
 * frame's compile pass.
 */

import type {
  AnimatableProps,
  KeyframeTrack,
  SceneNode,
} from "./sceneGraph";

export type Easing = (t: number) => number; // t in [0,1] → [0,1]

export const easings = {
  linear: (t: number) => t,
  easeInCubic: (t: number) => t * t * t,
  easeOutCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  easeInOutCubic: (t: number) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
  easeOutQuart: (t: number) => 1 - Math.pow(1 - t, 4),
  easeInOutQuart: (t: number) =>
    t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2,
  easeOutBack: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
};

/** Parse a CSS easing string into an Easing function. */
export function parseEasing(spec: string): Easing {
  spec = spec.trim();
  if (spec === "linear") return easings.linear;
  if (spec === "ease") return cubicBezier(0.25, 0.1, 0.25, 1);
  if (spec === "ease-in") return cubicBezier(0.42, 0, 1, 1);
  if (spec === "ease-out") return cubicBezier(0, 0, 0.58, 1);
  if (spec === "ease-in-out") return cubicBezier(0.42, 0, 0.58, 1);
  const bezier = spec.match(/^cubic-bezier\(\s*([\d.-]+)\s*,\s*([\d.-]+)\s*,\s*([\d.-]+)\s*,\s*([\d.-]+)\s*\)$/);
  if (bezier) {
    return cubicBezier(
      Number(bezier[1]),
      Number(bezier[2]),
      Number(bezier[3]),
      Number(bezier[4]),
    );
  }
  return easings.linear;
}

/**
 * Compute the y-coordinate of a cubic-bezier curve at parameter t.
 * Reference: https://developer.mozilla.org/en-US/docs/Web/CSS/easing-function
 */
export function cubicBezier(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): Easing {
  const NEWTON_ITERATIONS = 4;
  const NEWTON_MIN_SLOPE = 0.001;
  const SUBDIVISION_PRECISION = 0.0000001;
  const SUBDIVISION_MAX_ITERATIONS = 10;
  const kSplineTableSize = 11;
  const kSampleStepSize = 1 / (kSplineTableSize - 1);

  const sampleValues = new Float32Array(kSplineTableSize);
  for (let i = 0; i < kSplineTableSize; i++) {
    sampleValues[i] = calcBezier(i * kSampleStepSize, x1, x2);
  }

  function calcBezier(t: number, a1: number, a2: number): number {
    return ((1 - 3 * a2 + 3 * a1) * t + (3 * a2 - 6 * a1)) * t + (3 * a1) * t;
  }
  function getSlope(t: number, a1: number, a2: number): number {
    return 3 * ((1 - 3 * a2 + 3 * a1) * t + (2 * a2 - 2 * a1)) * t + (3 * a1);
  }

  function binarySubdivide(
    aX: number,
    aA: number,
    aB: number,
  ): number {
    let currentX = 0;
    let currentT = 0;
    let i = 0;
    do {
      currentT = aA + (aB - aA) / 2;
      currentX = calcBezier(currentT, x1, x2) - aX;
      if (currentX > 0) aB = currentT;
      else aA = currentT;
    } while (Math.abs(currentX) > SUBDIVISION_PRECISION && ++i < SUBDIVISION_MAX_ITERATIONS);
    return currentT;
  }

  function newtonRaphsonIterate(aX: number, aGuessT: number): number {
    for (let i = 0; i < NEWTON_ITERATIONS; i++) {
      const slope = getSlope(aGuessT, x1, x2);
      if (slope === 0) return aGuessT;
      const currentX = calcBezier(aGuessT, x1, x2) - aX;
      aGuessT -= currentX / slope;
    }
    return aGuessT;
  }

  function getTForX(aX: number): number {
    let intervalStart = 0;
    let currentSample = 1;
    const lastSample = kSplineTableSize - 1;
    for (; currentSample !== lastSample && sampleValues[currentSample] <= aX; ++currentSample) {
      intervalStart += kSampleStepSize;
    }
    --currentSample;
    const start = intervalStart;
    const dist = (aX - sampleValues[currentSample]) / (sampleValues[currentSample + 1] - sampleValues[currentSample]);
    const guessForT = start + dist * kSampleStepSize;
    const initialSlope = getSlope(guessForT, x1, x2);
    if (initialSlope >= NEWTON_MIN_SLOPE) {
      return newtonRaphsonIterate(aX, guessForT);
    }
    if (initialSlope === 0) return guessForT;
    return binarySubdivide(aX, intervalStart, intervalStart + kSampleStepSize);
  }

  return (t: number) => calcBezier(getTForX(t), y1, y2);
}

/** Linear interpolation between two values. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Sample a keyframe track at the given progress in [0,1]. Returns the
 *  interpolated property set, or null if no keyframes exist. */
export function sampleKeyframeTrack(
  track: KeyframeTrack,
  progress: number,
): Partial<AnimatableProps> {
  const frames = track.keyframes;
  if (frames.length === 0) return {};
  if (progress <= frames[0].at) {
    // Fill backwards: use first frame's props.
    if (track.fillMode === "backwards" || track.fillMode === "both") {
      return frames[0].props;
    }
    return {};
  }
  const last = frames[frames.length - 1];
  if (progress >= last.at) {
    if (track.fillMode === "forwards" || track.fillMode === "both") {
      return last.props;
    }
    return {};
  }
  for (let i = 0; i < frames.length - 1; i++) {
    const a = frames[i];
    const b = frames[i + 1];
    if (progress >= a.at && progress < b.at) {
      const span = b.at - a.at;
      const k = span > 0 ? (progress - a.at) / span : 0;
      const e = parseEasing(a.easing ?? track.defaultEasing ?? "linear");
      const eased = e(k);
      return interpProps(a.props, b.props, eased);
    }
  }
  return last.props;
}

function interpProps(
  a: Partial<AnimatableProps>,
  b: Partial<AnimatableProps>,
  t: number,
): Partial<AnimatableProps> {
  const out: Partial<AnimatableProps> = {};
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    const av = (a as Record<string, unknown>)[k];
    const bv = (b as Record<string, unknown>)[k];
    if (typeof av === "number" && typeof bv === "number") {
      (out as Record<string, unknown>)[k] = lerp(av, bv, t);
    } else if (typeof av === "number") {
      (out as Record<string, unknown>)[k] = av;
    } else if (typeof bv === "number") {
      (out as Record<string, unknown>)[k] = bv;
    }
  }
  return out;
}

/**
 * Advance an animation track on a node by dt seconds. Mutates the node's
 * properties in place. Returns true if the track is still running.
 *
 * If the track has finished and fill-mode is "none", we don't touch the
 * properties — the node keeps its last-interpolated values.
 */
export function tickAnimation(
  node: SceneNode,
  track: KeyframeTrack,
  dt: number,
): boolean {
  if (track.paused) return true;
  if (track.duration <= 0) return false;

  // Account for delay.
  if (track.delay > 0) {
    track.delay -= dt;
    if (track.delay > 0) return true;
    // We're past the delay — spend the remaining dt on the first iteration.
    dt = -track.delay;
  }

  // Scroll-driven tracks have their elapsedInIteration already set by
  // tickScroll. Skip the dt accumulator — sample the keyframe as-is.
  if (!track.scrollTrack) {
    track.elapsedInIteration += dt;
    // Advance iterations.
    while (track.elapsedInIteration >= track.duration) {
      track.elapsedInIteration -= track.duration;
      track.currentIteration++;
      if (track.iterations !== "infinite" && track.currentIteration >= track.iterations) {
        // Final iteration finished.
        applyFinalFrame(node, track);
        return false;
      }
    }
  }

  let progress = track.elapsedInIteration / track.duration;
  if (track.direction === "reverse") progress = 1 - progress;
  else if (track.direction === "alternate" && track.currentIteration % 2 === 1) {
    progress = 1 - progress;
  } else if (track.direction === "alternate-reverse" && track.currentIteration % 2 === 0) {
    progress = 1 - progress;
  }
  const sampled = sampleKeyframeTrack(track, progress);
  applyToNode(node, sampled);
  return true;
}

function applyFinalFrame(node: SceneNode, track: KeyframeTrack): void {
  const frames = track.keyframes;
  if (frames.length === 0) return;
  const last = frames[frames.length - 1];
  if (track.fillMode === "forwards" || track.fillMode === "both") {
    applyToNode(node, last.props);
  }
}

function applyToNode(node: SceneNode, props: Partial<AnimatableProps>): void {
  if (props.x !== undefined) node.x = props.x;
  if (props.y !== undefined) node.y = props.y;
  if (props.opacity !== undefined) node.opacity = props.opacity;
  if (props.rotation !== undefined) node.rotation = props.rotation;
  if (props.scaleX !== undefined) node.scaleX = props.scaleX;
  if (props.scaleY !== undefined) node.scaleY = props.scaleY;
  if (props.fillR !== undefined) node.fill.r = props.fillR;
  if (props.fillG !== undefined) node.fill.g = props.fillG;
  if (props.fillB !== undefined) node.fill.b = props.fillB;
  if (props.fillA !== undefined) node.fill.a = props.fillA;
  if (props.radius !== undefined) {
    node.radii = [props.radius, props.radius, props.radius, props.radius];
  }
}